import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  fetchMessages,
  fetchQuotes,
  fetchAppointments,
  fetchAiLeads,
  updateMessageStatus,
  archiveMessage,
  updateQuoteStatus,
  archiveQuote,
  updateAppointmentStatus,
  archiveAppointment,
  type ContactMessageApi,
  type QuoteRequestApi,
  type AppointmentLeadApi,
} from '@/api/client';

interface RowStatus {
  status: string;
}

type TabId = 'contact' | 'quotes' | 'appointments' | 'ai_leads';

const statusColors: Record<string, string> = {
  New: 'bg-amber-100 text-amber-700',
  Contacted: 'bg-blue-100 text-blue-700',
  Resolved: 'bg-green-100 text-green-700',
  new: 'bg-amber-100 text-amber-700',
  reviewing: 'bg-pink-100 text-pink-700',
  quoted: 'bg-cyan-100 text-cyan-700',
  contacted: 'bg-blue-100 text-blue-700',
  won: 'bg-green-100 text-green-700',
  lost: 'bg-red-100 text-red-700',
  archived: 'bg-gray-100 text-gray-600',
  scheduled: 'bg-teal-100 text-teal-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

function getInitials(fullName: string) {
  return fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatDate(value?: string) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

export default function AdminMessages() {
  const { t } = useTranslation('home');
  const [activeTab, setActiveTab] = useState<TabId>('contact');

  const [messages, setMessages] = useState<ContactMessageApi[]>([]);
  const [quotes, setQuotes] = useState<QuoteRequestApi[]>([]);
  const [appointments, setAppointments] = useState<AppointmentLeadApi[]>([]);
  const [aiLeads, setAiLeads] = useState<AppointmentLeadApi[]>([]);

  const [loaded, setLoaded] = useState<Record<TabId, boolean>>({
    contact: false,
    quotes: false,
    appointments: false,
    ai_leads: false,
  });
  const [loading, setLoading] = useState<Record<TabId, boolean>>({
    contact: true,
    quotes: false,
    appointments: false,
    ai_leads: false,
  });
  const [tabError, setTabError] = useState<Record<TabId, string | null>>({
    contact: null,
    quotes: null,
    appointments: null,
    ai_leads: null,
  });

  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [quoteFilterStatus, setQuoteFilterStatus] = useState<string>('all');
  const [appointmentFilterStatus, setAppointmentFilterStatus] = useState<string>('all');
  const [viewingItem, setViewingItem] = useState<ContactMessageApi | QuoteRequestApi | AppointmentLeadApi | null>(null);

  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'contact', label: t('admin_tab_contact'), icon: 'ri-mail-unread-line' },
    { id: 'quotes', label: t('admin_tab_quotes'), icon: 'ri-file-list-3-line' },
    { id: 'appointments', label: t('admin_tab_appointments'), icon: 'ri-calendar-check-line' },
    { id: 'ai_leads', label: t('admin_tab_ai_leads'), icon: 'ri-sparkling-line' },
  ];

  const loadContact = useCallback(async () => {
    setLoading((prev) => ({ ...prev, contact: true }));
    setTabError((prev) => ({ ...prev, contact: null }));
    try {
      const data = await fetchMessages();
      setMessages(data);
    } catch (err) {
      setTabError((prev) => ({ ...prev, contact: err instanceof Error ? err.message : 'Failed to load' }));
    } finally {
      setLoading((prev) => ({ ...prev, contact: false }));
      setLoaded((prev) => ({ ...prev, contact: true }));
    }
  }, []);

  const loadQuotes = useCallback(async () => {
    setLoading((prev) => ({ ...prev, quotes: true }));
    setTabError((prev) => ({ ...prev, quotes: null }));
    try {
      const data = await fetchQuotes();
      setQuotes(data);
    } catch (err) {
      setTabError((prev) => ({ ...prev, quotes: err instanceof Error ? err.message : 'Failed to load' }));
    } finally {
      setLoading((prev) => ({ ...prev, quotes: false }));
      setLoaded((prev) => ({ ...prev, quotes: true }));
    }
  }, []);

  const loadAppointments = useCallback(async () => {
    setLoading((prev) => ({ ...prev, appointments: true }));
    setTabError((prev) => ({ ...prev, appointments: null }));
    try {
      const data = await fetchAppointments();
      // fetchAppointments() already excludes source='ai_assistant' at the API layer
      setAppointments(data);
    } catch (err) {
      setTabError((prev) => ({ ...prev, appointments: err instanceof Error ? err.message : 'Failed to load' }));
    } finally {
      setLoading((prev) => ({ ...prev, appointments: false }));
      setLoaded((prev) => ({ ...prev, appointments: true }));
    }
  }, []);

  const loadAiLeads = useCallback(async () => {
    setLoading((prev) => ({ ...prev, ai_leads: true }));
    setTabError((prev) => ({ ...prev, ai_leads: null }));
    try {
      const data = await fetchAiLeads();
      setAiLeads(data);
    } catch (err) {
      setTabError((prev) => ({ ...prev, ai_leads: err instanceof Error ? err.message : 'Failed to load' }));
    } finally {
      setLoading((prev) => ({ ...prev, ai_leads: false }));
      setLoaded((prev) => ({ ...prev, ai_leads: true }));
    }
  }, []);

  useEffect(() => {
    loadContact();
  }, [loadContact]);

  const handleRefresh = () => {
    if (activeTab === 'contact') loadContact();
    if (activeTab === 'quotes') loadQuotes();
    if (activeTab === 'appointments') loadAppointments();
    if (activeTab === 'ai_leads') loadAiLeads();
  };

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    if (tabId === 'quotes') setQuoteFilterStatus('all');
    else if (tabId === 'appointments' || tabId === 'ai_leads') setAppointmentFilterStatus('all');
    else setFilterStatus('all');

    // Load data on first visit to the tab — set loading true immediately so spinner shows
    if (tabId === 'quotes' && !loaded.quotes) {
      setLoading((prev) => ({ ...prev, quotes: true }));
      loadQuotes();
    }
    if (tabId === 'appointments' && !loaded.appointments) {
      setLoading((prev) => ({ ...prev, appointments: true }));
      loadAppointments();
    }
    if (tabId === 'ai_leads' && !loaded.ai_leads) {
      setLoading((prev) => ({ ...prev, ai_leads: true }));
      loadAiLeads();
    }
  };

  const getStatusLabel = (status: string) => {
    const keyMap: Record<string, string> = {
      New: 'admin_status_new',
      Contacted: 'admin_status_contacted',
      Resolved: 'admin_status_resolved',
      new: 'admin_status_new',
      reviewing: 'admin_status_reviewing',
      quoted: 'admin_status_quoted',
      contacted: 'admin_status_contacted',
      won: 'admin_status_won',
      lost: 'admin_status_lost',
      archived: 'admin_status_archived',
      scheduled: 'admin_status_scheduled',
      completed: 'admin_status_completed',
      cancelled: 'admin_status_cancelled',
    };
    return t(keyMap[status] || 'admin_status_new');
  };

  // --- Appointments & AI Leads: real API helpers ---
  const updateAppointmentInState = (id: number, newStatus: AppointmentLeadApi['status']) => {
    setAppointments((prev) => prev.map((m) => (m.id === id ? { ...m, status: newStatus } : m)));
    setAiLeads((prev) => prev.map((m) => (m.id === id ? { ...m, status: newStatus } : m)));
    if (viewingItem?.id === id) {
      setViewingItem((prev) => (prev && prev.id === id ? { ...prev, status: newStatus } as AppointmentLeadApi : prev));
    }
  };

  const removeAppointmentFromState = (id: number) => {
    setAppointments((prev) => prev.filter((m) => m.id !== id));
    setAiLeads((prev) => prev.filter((m) => m.id !== id));
    if (viewingItem?.id === id) setViewingItem(null);
  };

  // Mark Contacted — real API for contact, quotes, appointments
  const markContacted = async (id: number, list: 'messages' | 'quotes' | 'appointments' | 'ai_leads', closeModal = false) => {
    if (list === 'messages') {
      const res = await updateMessageStatus({ id, status: 'Contacted' });
      if (!res.success) {
        Swal.fire({ title: t('admin_error'), text: t('admin_status_update_error'), icon: 'error', confirmButtonColor: '#ef4444' });
        return;
      }
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'Contacted' as const } : m)));
    } else if (list === 'quotes') {
      const res = await updateQuoteStatus({ id, status: 'contacted' });
      if (!res.success) {
        Swal.fire({ title: t('admin_error'), text: t('admin_status_update_error'), icon: 'error', confirmButtonColor: '#ef4444' });
        return;
      }
      setQuotes((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'contacted' as const } : m)));
    } else {
      const res = await updateAppointmentStatus({ id, status: 'contacted' });
      if (!res.success) {
        Swal.fire({ title: t('admin_error'), text: t('admin_status_update_error'), icon: 'error', confirmButtonColor: '#ef4444' });
        return;
      }
      updateAppointmentInState(id, 'contacted');
    }
    Swal.fire({ title: t('admin_status_updated'), icon: 'success', confirmButtonColor: '#3DBFA0', timer: 1500, timerProgressBar: true });
    if (closeModal) setViewingItem(null);
  };

  // Mark Resolved — real API for contact; mark completed for appointments; won for quotes
  const markResolved = async (id: number, list: 'messages' | 'quotes' | 'appointments' | 'ai_leads', closeModal = false) => {
    if (list === 'messages') {
      const res = await updateMessageStatus({ id, status: 'Resolved' });
      if (!res.success) {
        Swal.fire({ title: t('admin_error'), text: t('admin_status_update_error'), icon: 'error', confirmButtonColor: '#ef4444' });
        return;
      }
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'Resolved' as const } : m)));
    } else if (list === 'quotes') {
      const res = await updateQuoteStatus({ id, status: 'won' });
      if (!res.success) {
        Swal.fire({ title: t('admin_error'), text: t('admin_status_update_error'), icon: 'error', confirmButtonColor: '#ef4444' });
        return;
      }
      setQuotes((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'won' as const } : m)));
      if (viewingItem?.id === id) {
        setViewingItem((prev) => (prev && prev.id === id ? { ...prev, status: 'won' } as QuoteRequestApi : prev));
      }
    } else {
      const res = await updateAppointmentStatus({ id, status: 'completed' });
      if (!res.success) {
        Swal.fire({ title: t('admin_error'), text: t('admin_status_update_error'), icon: 'error', confirmButtonColor: '#ef4444' });
        return;
      }
      updateAppointmentInState(id, 'completed');
    }
    Swal.fire({ title: t('admin_status_updated'), icon: 'success', confirmButtonColor: '#3DBFA0', timer: 1500, timerProgressBar: true });
    if (closeModal) setViewingItem(null);
  };

  // Archive — real API for contact, quotes, appointments
  const handleArchive = async (item: ContactMessageApi | QuoteRequestApi | AppointmentLeadApi, list: 'messages' | 'quotes' | 'appointments' | 'ai_leads') => {
    const result = await Swal.fire({
      title: `${t('admin_archive')}?`,
      text: `${t('admin_archive_confirm')}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1A2E4A',
      cancelButtonColor: '#9ca3af',
      confirmButtonText: t('admin_archive'),
      cancelButtonText: t('admin_cancel'),
    });

    if (!result.isConfirmed) return;

    if (list === 'messages') {
      const res = await archiveMessage(item.id);
      if (!res.success) { Swal.fire({ title: t('admin_error'), text: t('admin_archive_error'), icon: 'error', confirmButtonColor: '#ef4444' }); return; }
      setMessages((prev) => prev.filter((m) => m.id !== item.id));
    } else if (list === 'quotes') {
      const res = await archiveQuote(item.id);
      if (!res.success) { Swal.fire({ title: t('admin_error'), text: t('admin_archive_error'), icon: 'error', confirmButtonColor: '#ef4444' }); return; }
      setQuotes((prev) => prev.filter((m) => m.id !== item.id));
    } else {
      const res = await archiveAppointment(item.id);
      if (!res.success) { Swal.fire({ title: t('admin_error'), text: t('admin_archive_error'), icon: 'error', confirmButtonColor: '#ef4444' }); return; }
      removeAppointmentFromState(item.id);
    }
    Swal.fire({
      title: t('admin_archived'),
      icon: 'success',
      confirmButtonColor: '#3DBFA0',
      timer: 1500,
      timerProgressBar: true,
    });
  };

  // Quote status dropdown
  const handleChangeQuoteStatus = async (id: number, newStatus: QuoteRequestApi['status']) => {
    const res = await updateQuoteStatus({ id, status: newStatus });
    if (!res.success) {
      Swal.fire({ title: t('admin_error'), text: t('admin_status_update_error'), icon: 'error', confirmButtonColor: '#ef4444' });
      return;
    }
    setQuotes((prev) => prev.map((m) => (m.id === id ? { ...m, status: newStatus } : m)));
    Swal.fire({ title: t('admin_status_updated'), icon: 'success', confirmButtonColor: '#3DBFA0', timer: 1200, timerProgressBar: true });
    if (viewingItem?.id === id) {
      setViewingItem((prev) => (prev && prev.id === id ? { ...prev, status: newStatus } as QuoteRequestApi : prev));
    }
  };

  // Appointment status dropdown
  const handleChangeAppointmentStatus = async (id: number, newStatus: AppointmentLeadApi['status']) => {
    const res = await updateAppointmentStatus({ id, status: newStatus });
    if (!res.success) {
      Swal.fire({ title: t('admin_error'), text: t('admin_status_update_error'), icon: 'error', confirmButtonColor: '#ef4444' });
      return;
    }
    updateAppointmentInState(id, newStatus);
    Swal.fire({ title: t('admin_status_updated'), icon: 'success', confirmButtonColor: '#3DBFA0', timer: 1200, timerProgressBar: true });
  };

  const handleSendEmail = (item: ContactMessageApi | QuoteRequestApi | AppointmentLeadApi) => {
    const email = 'email' in item ? item.email : '';
    Swal.fire({
      title: t('admin_send_email'),
      html: `<p class="text-sm">${t('admin_email_body')} <strong>${email}</strong></p><p class="text-xs text-gray-500 mt-2">${t('admin_email_note')}</p>`,
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3DBFA0',
      cancelButtonColor: '#1A2E4A',
      confirmButtonText: t('admin_open_email_app'),
      cancelButtonText: t('admin_cancel'),
    }).then((result) => {
      if (result.isConfirmed) window.open(`mailto:${email}?subject=Re: Complete Home Care Cleaning`, '_blank');
    });
  };

  const handleSendWhatsApp = (item: ContactMessageApi | QuoteRequestApi | AppointmentLeadApi) => {
    const phone = 'phone' in item ? item.phone : '';
    const name = 'full_name' in item ? item.full_name : 'name' in item ? item.name : '';
    const cleanPhone = phone.replace(/\D/g, '');
    Swal.fire({
      title: t('admin_send_whatsapp'),
      html: `<p class="text-sm">${t('admin_whatsapp_body')} <strong>${name}</strong>?</p><p class="text-xs text-gray-500 mt-2">${t('admin_whatsapp_note')} ${phone}</p>`,
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3DBFA0',
      cancelButtonColor: '#1A2E4A',
      confirmButtonText: t('admin_open_whatsapp'),
      cancelButtonText: t('admin_cancel'),
    }).then((result) => {
      if (result.isConfirmed) window.open(`https://wa.me/1${cleanPhone}`, '_blank');
    });
  };

  const currentList = () => {
    if (activeTab === 'contact') return messages;
    if (activeTab === 'quotes') return quotes;
    if (activeTab === 'appointments') return appointments;
    return aiLeads;
  };

  const currentListKey = (): 'messages' | 'quotes' | 'appointments' | 'ai_leads' => {
    if (activeTab === 'contact') return 'messages';
    if (activeTab === 'quotes') return 'quotes';
    if (activeTab === 'appointments') return 'appointments';
    return 'ai_leads';
  };

  const isLoading = loading[activeTab];
  const currentError = tabError[activeTab];
  const data = currentList();

  const filtered = (() => {
    if (activeTab === 'quotes') {
      return quoteFilterStatus === 'all' ? data : data.filter((m) => (m as RowStatus).status === quoteFilterStatus);
    }
    if (activeTab === 'appointments') {
      return appointmentFilterStatus === 'all' ? data : data.filter((m) => (m as RowStatus).status === appointmentFilterStatus);
    }
    if (activeTab === 'ai_leads') {
      return appointmentFilterStatus === 'all' ? data : data.filter((m) => (m as RowStatus).status === appointmentFilterStatus);
    }
    return filterStatus === 'all' ? data : data.filter((m) => (m as RowStatus).status === filterStatus);
  })();

  const newCount = messages.filter((m) => m.status === 'New').length;
  const quoteNewCount = quotes.filter((m) => m.status === 'new').length;
  const apptNewCount = appointments.filter((m) => m.status === 'new').length;
  const aiNewCount = aiLeads.filter((m) => m.status === 'new').length;

  const tabBadge = (tabId: TabId) => {
    if (tabId === 'contact') return newCount;
    if (tabId === 'quotes') return quoteNewCount;
    if (tabId === 'appointments') return apptNewCount;
    return aiNewCount;
  };

  // Appointments status filter with counts
  const appointmentStatuses = ['all', 'new', 'contacted', 'scheduled', 'completed', 'cancelled', 'archived'];

  return (
    <div className="space-y-6 max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-brand-dark font-heading">{t('admin_messages')}</h2>
          <p className="text-sm text-brand-muted mt-1">{t('admin_view_manage')}</p>
        </div>
        <button
          onClick={handleRefresh}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-brand-dark text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />
          {t('admin_refresh')}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-100 p-1 flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab.id ? 'bg-brand-dark text-white' : 'text-brand-muted hover:bg-gray-50'
            }`}
          >
            <i className={`${tab.icon} text-sm`} />
            {tab.label}
            {tabBadge(tab.id) > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{tabBadge(tab.id)}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      {activeTab === 'contact' && (
        <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-100 p-1 w-fit">
          {['all', 'New', 'Contacted', 'Resolved'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                filterStatus === status ? 'bg-brand-dark text-white' : 'text-brand-muted hover:bg-gray-50'
              }`}
            >
              {status === 'all' ? t('admin_all_filter') : t(`admin_${status.toLowerCase()}_count`)}
              {status !== 'all' && <span className="ml-1.5">{messages.filter((m) => m.status === status).length}</span>}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'quotes' && (
        <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-100 p-1 w-fit">
          {['all', 'new', 'reviewing', 'quoted', 'contacted', 'won', 'lost', 'archived'].map((status) => (
            <button
              key={status}
              onClick={() => setQuoteFilterStatus(status)}
              className={`px-4 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                quoteFilterStatus === status ? 'bg-brand-dark text-white' : 'text-brand-muted hover:bg-gray-50'
              }`}
            >
              {status === 'all' ? t('admin_all_filter') : getStatusLabel(status)}
              {status !== 'all' && <span className="ml-1.5">{quotes.filter((m) => m.status === status).length}</span>}
            </button>
          ))}
        </div>
      )}

      {(activeTab === 'appointments' || activeTab === 'ai_leads') && (
        <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-100 p-1 w-fit">
          {appointmentStatuses.map((status) => (
            <button
              key={status}
              onClick={() => setAppointmentFilterStatus(status)}
              className={`px-4 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                appointmentFilterStatus === status ? 'bg-brand-dark text-white' : 'text-brand-muted hover:bg-gray-50'
              }`}
            >
              {status === 'all' ? t('admin_all_filter') : getStatusLabel(status)}
              {status !== 'all' && (
                <span className="ml-1.5">
                  {(activeTab === 'ai_leads' ? aiLeads : appointments).filter((m) => m.status === status).length}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Error state */}
      {!isLoading && currentError && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-red-100 bg-red-50">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 shrink-0">
            <i className="ri-wifi-off-line text-red-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800">Backend not reachable</p>
            <p className="text-xs text-red-700 mt-0.5">{currentError}</p>
          </div>
          <button
            onClick={handleRefresh}
            className="shrink-0 px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />Retry
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <div className="w-10 h-10 border-2 border-brand-mint border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm font-medium text-brand-dark">{t('admin_loading')}</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[850px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_name')}</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_phone')}</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_email')}</th>
                  {activeTab === 'contact' && (
                    <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_service_type')}</th>
                  )}
                  {activeTab === 'quotes' && (
                    <>
                      <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_property_type')}</th>
                      <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_cleaning_type')}</th>
                      <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_frequency')}</th>
                    </>
                  )}
                  {(activeTab === 'appointments' || activeTab === 'ai_leads') && (
                    <>
                      <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_appointment_type')}</th>
                      <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_service_type')}</th>
                      <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_preferred_date')}</th>
                      <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_preferred_time')}</th>
                    </>
                  )}
                  <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_status')}</th>
                  {(activeTab === 'appointments' || activeTab === 'ai_leads') && (
                    <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_source')}</th>
                  )}
                  <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">{t('admin_created_at')}</th>
                  <th className="text-right px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider w-28">{t('admin_actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((item) => (
                  <tr
                    key={`${activeTab}-${item.id}`}
                    onClick={() => setViewingItem(item)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewingItem(item); } }}
                    role="button"
                    tabIndex={0}
                    title="Click to view details"
                    className={`cursor-pointer hover:bg-brand-mint/5 active:bg-brand-mint/10 transition-colors select-none outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-mint ${(item as RowStatus).status === 'New' || (item as RowStatus).status === 'new' ? 'bg-amber-50/30 hover:bg-amber-50/60' : ''}`}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-brand-light flex items-center justify-center text-xs font-bold text-brand-dark shrink-0">
                          {getInitials((item as Record<string, string>).full_name || (item as Record<string, string>).name || '')}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-brand-dark whitespace-nowrap">
                            {(item as Record<string, string>).full_name || (item as Record<string, string>).name || ''}
                          </span>
                          {activeTab === 'ai_leads' && (
                            <span className="inline-flex items-center self-start mt-0.5 px-1.5 py-0.5 bg-gradient-to-r from-teal-500 to-brand-mint text-white text-[10px] font-bold rounded-full uppercase tracking-wide">
                              <i className="ri-sparkling-line mr-1" />{t('admin_ai_assistant')}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">{(item as Record<string, string>).phone || ''}</td>
                    <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">{(item as Record<string, string>).email || ''}</td>
                    {activeTab === 'contact' && (
                      <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">{(item as ContactMessageApi).service_type || ''}</td>
                    )}
                    {activeTab === 'quotes' && (
                      <>
                        <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">{(item as QuoteRequestApi).property_type || ''}</td>
                        <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">{(item as QuoteRequestApi).cleaning_type || ''}</td>
                        <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">{(item as QuoteRequestApi).cleaning_frequency || ''}</td>
                      </>
                    )}
                    {(activeTab === 'appointments' || activeTab === 'ai_leads') && (
                      <>
                        <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">{(item as AppointmentLeadApi).appointment_type || ''}</td>
                        <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">{(item as AppointmentLeadApi).service_type || ''}</td>
                        <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">{formatDate((item as AppointmentLeadApi).preferred_date)}</td>
                        <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">{(item as AppointmentLeadApi).preferred_time || ''}</td>
                      </>
                    )}
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${statusColors[(item as RowStatus).status] || 'bg-gray-100 text-gray-600'}`}>
                        {getStatusLabel((item as RowStatus).status)}
                      </span>
                    </td>
                    {(activeTab === 'appointments' || activeTab === 'ai_leads') && (
                      <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">
                        {(item as AppointmentLeadApi).source === 'ai_assistant' ? t('admin_ai_assistant') : ((item as AppointmentLeadApi).source || 'Website')}
                      </td>
                    )}
                    <td className="px-5 py-4 text-sm text-brand-muted whitespace-nowrap">{formatDate((item as Record<string, string>).created_at)}</td>
                    <td className="px-5 py-4 text-right">
                      <div
                        className="flex items-center justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {((item as RowStatus).status === 'New' || (item as RowStatus).status === 'new') && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markContacted(item.id, currentListKey()); }}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-blue-50 transition-colors cursor-pointer"
                            aria-label={t('admin_mark_contacted')}
                            title={t('admin_mark_contacted')}
                          >
                            <i className="ri-phone-line text-blue-500" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleArchive(item, currentListKey()); }}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                          aria-label={t('admin_archive')}
                          title={t('admin_archive')}
                        >
                          <i className="ri-archive-line text-brand-muted" />
                        </button>
                        {/* Chevron hint — purely decorative, click propagates to row */}
                        <span className="w-6 h-6 flex items-center justify-center text-gray-300 pointer-events-none" aria-hidden="true">
                          <i className="ri-arrow-right-s-line text-lg" />
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && (
            <div className="py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-gray-300 text-3xl" />
              </div>
              <p className="text-sm font-medium text-brand-dark">{t('admin_empty')}</p>
              <p className="text-xs text-brand-muted mt-1">
                {activeTab === 'contact' && t('admin_incoming_messages')}
                {activeTab === 'quotes' && t('admin_incoming_quotes')}
                {activeTab === 'appointments' && t('admin_incoming_appointments')}
                {activeTab === 'ai_leads' && t('admin_incoming_ai_leads')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {viewingItem && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-dark/40 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setViewingItem(null); }}
        >
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl my-8 animate-[fadeIn_0.2s_ease-out]">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center text-sm font-bold text-brand-dark">
                  {getInitials((viewingItem as Record<string, string>).full_name || (viewingItem as Record<string, string>).name || '')}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-brand-dark font-heading">
                    {(viewingItem as Record<string, string>).full_name || (viewingItem as Record<string, string>).name || ''}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusColors[(viewingItem as RowStatus).status]}`}>
                      {getStatusLabel((viewingItem as RowStatus).status)}
                    </span>
                    {activeTab === 'contact' && (
                      <span className="text-xs text-brand-muted">{(viewingItem as ContactMessageApi).service_type}</span>
                    )}
                    {activeTab === 'quotes' && (
                      <span className="text-xs text-brand-muted">{(viewingItem as QuoteRequestApi).property_type} &middot; {(viewingItem as QuoteRequestApi).cleaning_type}</span>
                    )}
                    {(activeTab === 'appointments' || activeTab === 'ai_leads') && (
                      <span className="text-xs text-brand-muted">{(viewingItem as AppointmentLeadApi).appointment_type}</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setViewingItem(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-brand-dark" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Contact Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-brand-mint" />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{t('admin_phone')}</p>
                    <p className="text-sm font-medium text-brand-dark mt-0.5">{(viewingItem as Record<string, string>).phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-brand-mint" />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{t('admin_email')}</p>
                    <p className="text-sm font-medium text-brand-dark mt-0.5">{(viewingItem as Record<string, string>).email}</p>
                  </div>
                </div>
                {(viewingItem as ContactMessageApi | AppointmentLeadApi | QuoteRequestApi).property_address && (
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line text-brand-mint" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{t('admin_property_address')}</p>
                      <p className="text-sm font-medium text-brand-dark mt-0.5">{(viewingItem as ContactMessageApi | AppointmentLeadApi | QuoteRequestApi).property_address}</p>
                    </div>
                  </div>
                )}
                {(viewingItem as Record<string, string>).preferred_date && (
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
                      <i className="ri-calendar-line text-brand-mint" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{t('admin_preferred_date')}</p>
                      <p className="text-sm font-medium text-brand-dark mt-0.5">{formatDate((viewingItem as Record<string, string>).preferred_date)}</p>
                    </div>
                  </div>
                )}
                {(viewingItem as Record<string, string>).preferred_time && (
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
                      <i className="ri-time-line text-brand-mint" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{t('admin_preferred_time')}</p>
                      <p className="text-sm font-medium text-brand-dark mt-0.5">{(viewingItem as Record<string, string>).preferred_time}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Quote Detail Fields */}
              {activeTab === 'quotes' && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: t('admin_bedrooms'), value: (viewingItem as QuoteRequestApi).bedrooms },
                      { label: t('admin_bathrooms'), value: (viewingItem as QuoteRequestApi).bathrooms },
                      { label: t('admin_kitchens'), value: (viewingItem as QuoteRequestApi).kitchens },
                      { label: t('admin_living_rooms'), value: (viewingItem as QuoteRequestApi).living_rooms },
                      { label: t('admin_dining_rooms'), value: (viewingItem as QuoteRequestApi).dining_rooms },
                      { label: t('admin_offices'), value: (viewingItem as QuoteRequestApi).offices },
                      { label: t('admin_garage'), value: (viewingItem as QuoteRequestApi).garage },
                      { label: t('admin_square_feet'), value: (viewingItem as QuoteRequestApi).square_feet },
                    ].map((field) => (
                      field.value ? (
                        <div key={field.label} className="p-3 bg-gray-50 rounded-xl">
                          <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{field.label}</p>
                          <p className="text-sm font-medium text-brand-dark mt-0.5">{field.value}</p>
                        </div>
                      ) : null
                    ))}
                    {(viewingItem as QuoteRequestApi).budget_range && (
                      <div className="p-3 bg-gray-50 rounded-xl">
                        <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{t('admin_budget')}</p>
                        <p className="text-sm font-medium text-brand-dark mt-0.5">{(viewingItem as QuoteRequestApi).budget_range}</p>
                      </div>
                    )}
                    {(viewingItem as QuoteRequestApi).cleaning_frequency && (
                      <div className="p-3 bg-gray-50 rounded-xl">
                        <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{t('admin_frequency')}</p>
                        <p className="text-sm font-medium text-brand-dark mt-0.5">{(viewingItem as QuoteRequestApi).cleaning_frequency}</p>
                      </div>
                    )}
                  </div>

                  {/* Extras */}
                  {(() => {
                    const quote = viewingItem as QuoteRequestApi;
                    let extras: string[] = [];
                    if (quote.extras && quote.extras.length > 0) extras = quote.extras;
                    else if (quote.extras_json) {
                      try {
                        const parsed = JSON.parse(quote.extras_json);
                        extras = Array.isArray(parsed) ? parsed : [];
                      } catch { /* ignore */ }
                    }
                    if (extras.length > 0) {
                      return (
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider mb-2">{t('admin_extras')}</p>
                          <div className="flex flex-wrap gap-2">
                            {extras.map((extra) => (
                              <span key={extra} className="inline-flex items-center px-2.5 py-1 bg-white rounded-lg text-xs font-medium text-brand-dark border border-gray-100">
                                {extra}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              )}

              {/* Appointment Type & Service */}
              {(activeTab === 'appointments' || activeTab === 'ai_leads') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
                      <i className="ri-calendar-check-line text-brand-mint" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{t('admin_appointment_type')}</p>
                      <p className="text-sm font-medium text-brand-dark mt-0.5">{(viewingItem as AppointmentLeadApi).appointment_type}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
                      <i className="ri-service-line text-brand-mint" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{t('admin_service_type')}</p>
                      <p className="text-sm font-medium text-brand-dark mt-0.5">{(viewingItem as AppointmentLeadApi).service_type}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Message / Notes */}
              {((viewingItem as ContactMessageApi).message || (viewingItem as QuoteRequestApi).notes || (viewingItem as AppointmentLeadApi).notes) && (
                <div>
                  <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider mb-2">{t('form_message')}</p>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-brand-dark leading-relaxed">
                      {(viewingItem as ContactMessageApi).message || (viewingItem as QuoteRequestApi).notes || (viewingItem as AppointmentLeadApi).notes}
                    </p>
                  </div>
                </div>
              )}

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-xs text-brand-muted">
                <span className="flex items-center gap-1"><i className="ri-time-line" />{t('admin_received')}: {formatDate((viewingItem as Record<string, string>).created_at)}</span>
                {(viewingItem as AppointmentLeadApi).source && (
                  <span className="flex items-center gap-1"><i className="ri-sparkling-line" />{t('admin_source')}: {(viewingItem as AppointmentLeadApi).source === 'ai_assistant' ? t('admin_ai_assistant') : (viewingItem as AppointmentLeadApi).source}</span>
                )}
                {(viewingItem as QuoteRequestApi).source && (
                  <span className="flex items-center gap-1"><i className="ri-sparkling-line" />{t('admin_source')}: {(viewingItem as QuoteRequestApi).source}</span>
                )}
              </div>

              {/* Quote Status Dropdown */}
              {activeTab === 'quotes' && (
                <div className="flex items-center gap-3 p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                  <i className="ri-arrow-up-down-line text-amber-600" />
                  <span className="text-sm font-medium text-brand-dark">{t('admin_status')}:</span>
                  <select
                    value={(viewingItem as QuoteRequestApi).status}
                    onChange={(e) => handleChangeQuoteStatus(viewingItem.id, e.target.value as QuoteRequestApi['status'])}
                    className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-mint cursor-pointer"
                  >
                    <option value="new">{getStatusLabel('new')}</option>
                    <option value="reviewing">{getStatusLabel('reviewing')}</option>
                    <option value="quoted">{getStatusLabel('quoted')}</option>
                    <option value="contacted">{getStatusLabel('contacted')}</option>
                    <option value="won">{getStatusLabel('won')}</option>
                    <option value="lost">{getStatusLabel('lost')}</option>
                    <option value="archived">{getStatusLabel('archived')}</option>
                  </select>
                </div>
              )}

              {/* Appointment Status Dropdown */}
              {(activeTab === 'appointments' || activeTab === 'ai_leads') && (
                <div className="flex items-center gap-3 p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                  <i className="ri-arrow-up-down-line text-amber-600" />
                  <span className="text-sm font-medium text-brand-dark">{t('admin_status')}:</span>
                  <select
                    value={(viewingItem as AppointmentLeadApi).status}
                    onChange={(e) => handleChangeAppointmentStatus(viewingItem.id, e.target.value as AppointmentLeadApi['status'])}
                    className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-mint cursor-pointer"
                  >
                    <option value="new">{getStatusLabel('new')}</option>
                    <option value="contacted">{getStatusLabel('contacted')}</option>
                    <option value="scheduled">{getStatusLabel('scheduled')}</option>
                    <option value="completed">{getStatusLabel('completed')}</option>
                    <option value="cancelled">{getStatusLabel('cancelled')}</option>
                    <option value="archived">{getStatusLabel('archived')}</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
              <button
                onClick={() => handleSendEmail(viewingItem)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-brand-dark text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <i className="ri-mail-send-line text-blue-500" />{t('admin_send_email')}
              </button>
              <button
                onClick={() => handleSendWhatsApp(viewingItem)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-brand-dark text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-green-500" />{t('admin_send_whatsapp')}
              </button>
              {((viewingItem as RowStatus).status === 'New' || (viewingItem as RowStatus).status === 'new') && (
                <button
                  onClick={() => markContacted(viewingItem.id, currentListKey(), true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors cursor-pointer"
                >
                  <i className="ri-phone-line" />{t('admin_mark_contacted')}
                </button>
              )}
              {activeTab !== 'quotes' && (viewingItem as RowStatus).status !== 'Resolved' && (viewingItem as RowStatus).status !== 'completed' && (
                <button
                  onClick={() => markResolved(viewingItem.id, currentListKey(), true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors cursor-pointer"
                >
                  <i className="ri-check-line" />{t('admin_mark_resolved')}
                </button>
              )}
              {activeTab === 'quotes' && (viewingItem as RowStatus).status !== 'won' && (viewingItem as RowStatus).status !== 'lost' && (
                <button
                  onClick={() => markResolved(viewingItem.id, currentListKey(), true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors cursor-pointer"
                >
                  <i className="ri-check-line" />{t('admin_mark_resolved')}
                </button>
              )}
              <button
                onClick={() => handleArchive(viewingItem, currentListKey())}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-brand-dark text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ml-auto"
              >
                <i className="ri-archive-line text-brand-muted" />{t('admin_archive')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}